import React, { useState, useEffect, useRef, useCallback } from 'react';
import { motion } from 'framer-motion';
import { useSacredAudio } from '../hooks/useSacredAudio';
import { useSpiritualStore } from '../hooks/useSpiritualStore';
import { WaveformType } from '../types/audio.types';
import { storage } from '../utils/storageManager';
import { Play, Pause, RotateCcw, Clock, Target, Heart } from 'lucide-react';
import toast from 'react-hot-toast';

/**
 * Sacred Meditation Timer Component
 * Embodying the infinite potential of 00
 * 
 * - Multiple meditation types (mindfulness, loving-kindness, body scan, etc.)
 * - 4-7-8 breath guidance with audio cues
 * - Intention setting for deeper practice
 * - Sacred frequency integration per meditation type
 * - Visual breathing rhythm animations
 * 
 * - Gentle completion bells, never jarring
 * - Full user control (pause, extend, end early)
 * - Clear time display and progress visualization
 * - Optional intention input (never required)
 * - Graceful audio cleanup
 * 
 */
interface MeditationTimerProps {
  onComplete?: (duration: number) => void;
}

const calculateSubBassFrequency = (baseFrequency: number): number => {
  let subBassFrequency = baseFrequency;

  while (subBassFrequency > 110) {
    subBassFrequency /= 2;
  }

  while (subBassFrequency < 40) {
    subBassFrequency *= 2;
  }

  return subBassFrequency;
};

const MeditationTimer: React.FC<MeditationTimerProps> = ({ onComplete }) => {
  const [duration, setDuration] = useState(10); // minutes
  const [timeLeft, setTimeLeft] = useState(duration * 60); // seconds
  const [isActive, setIsActive] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [meditationType, setMeditationType] = useState('mindfulness');
  const [intention, setIntention] = useState('');
  const [breathCount, setBreathCount] = useState(0);
  
  const { 
    playSacredTone, 
    stopAudio, 
    initializeAudio,
    isInitialized 
  } = useSacredAudio();
  const { startMeditation, isAuthenticated } = useSpiritualStore();
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const startTimeRef = useRef<number | null>(null);
  const handleCompleteRef = useRef<(elapsedSeconds: number) => Promise<void>>();

  const meditationTypes = [
    { id: 'mindfulness', name: 'Mindfulness', frequency: 66, color: '#2dd4bf', description: 'Present moment awareness' },
    { id: 'lovingkindness', name: 'Loving-Kindness', frequency: 80, color: '#fb7185', description: 'Compassion meditation' },
    { id: 'bodyscan', name: 'Body Scan', frequency: 50, color: '#34d399', description: 'Progressive relaxation' },
    { id: 'visualization', name: 'Visualization', frequency: 93, color: '#f59e0b', description: 'Creative imagery' },
    { id: 'breath', name: 'Breath Focus', frequency: 54, color: '#38bdf8', description: 'Breath awareness' },
  ];

  const selectedType = meditationTypes.find(t => t.id === meditationType) || meditationTypes[0];
  const timerVars = {
    '--timer-panel': '#07111f',
    '--timer-panel-soft': '#10223d',
    '--timer-text-primary': '#e2e8f0',
    '--timer-text-muted': '#94a3b8'
  } as React.CSSProperties;

  const playLayeredMeditationTone = useCallback((frequency: number, durationSeconds: number, waveType: WaveformType = 'sine') => {
    const subBassFrequency = calculateSubBassFrequency(frequency);
    playSacredTone(frequency, durationSeconds, waveType);
    playSacredTone(subBassFrequency, durationSeconds, waveType);
  }, [playSacredTone]);

  // ⚖️ Astraea: Audio initializes on user interaction (when meditation starts)
  // No need to initialize on mount - respect browser autoplay policies
  useEffect(() => {
    if (isInitialized) {
      console.log('🎵 MeditationTimer: Audio ready');
    }
  }, [isInitialized]);

  // Sync timeLeft when duration changes (only when timer is not active)
  useEffect(() => {
    if (!isActive) {
      setTimeLeft(duration * 60);
    }
  }, [duration, isActive]);

  // Handle meditation completion
  const handleComplete = useCallback(async (elapsedSeconds: number) => {
    setIsActive(false);
    setIsPaused(false);
    setBreathCount(0);
    startTimeRef.current = null;
    
    // Play completion bell
    if (isInitialized) {
      playLayeredMeditationTone(963, 4); // Divine connection frequency + matched undertone
    }
    
    // Save meditation session
    try {
      await storage.saveMeditationSession({
        type: meditationType,
        duration: elapsedSeconds,
        completedAt: new Date().toISOString(),
        intention: intention || undefined,
        breathCount: breathCount,
        frequency: selectedType.frequency
      });
      
      toast.success('🌟 Meditation complete and saved!');
    } catch (error) {
      console.error('Failed to save meditation:', error);
      toast.success('🌟 Meditation complete!');
    }
    
    onComplete?.(elapsedSeconds);
  }, [isInitialized, playLayeredMeditationTone, onComplete, meditationType, intention, breathCount, selectedType.frequency]);

  // Keep handleComplete ref up to date
  useEffect(() => {
    handleCompleteRef.current = handleComplete;
  }, [handleComplete]);

  // Timer logic
  useEffect(() => {
    // Clear any existing interval first
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }

    if (isActive && !isPaused) {
      intervalRef.current = setInterval(() => {
        setTimeLeft(prev => {
          if (prev <= 1) {
            // Clear interval immediately
            if (intervalRef.current) {
              clearInterval(intervalRef.current);
              intervalRef.current = null;
            }
            // Calculate elapsed time and call completion handler
            const elapsedSeconds = startTimeRef.current || duration * 60;
            if (handleCompleteRef.current) {
              handleCompleteRef.current(elapsedSeconds);
            }
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [isActive, isPaused, duration]);

  // Breath guide logic (4-7-8 breathing)
  useEffect(() => {
    if (isActive && !isPaused && meditationType === 'breath' && isInitialized) {
      const breathCycle = 19; // 4+7+8 seconds
      const breathInterval = setInterval(() => {
        setBreathCount(prev => prev + 1);
        const phase = breathCount % breathCycle;
        
        if (phase < 4) {
          // Inhale
          playLayeredMeditationTone(selectedType.frequency, 0.5);
        } else if (phase < 11) {
          // Hold
        } else {
          // Exhale
          playLayeredMeditationTone(selectedType.frequency * 0.8, 0.5);
        }
      }, 1000);

      return () => clearInterval(breathInterval);
    }
  }, [isActive, isPaused, breathCount, meditationType, isInitialized, playLayeredMeditationTone, selectedType.frequency]);

  const handleStart = async () => {
    if (!isActive) {
      // ✅ Initialize audio on user interaction (starting meditation)
      if (!isInitialized) {
        try {
          await initializeAudio();
        } catch (error) {
          console.error('Failed to initialize audio:', error);
        }
      }
      
      const initialTime = duration * 60;
      setTimeLeft(initialTime);
      startTimeRef.current = initialTime;
      setIsActive(true);
      setIsPaused(false);
      
      // Play starting bell
      if (isInitialized) {
        playLayeredMeditationTone(selectedType.frequency, 3);
      }
      
      // Save meditation session if authenticated
      if (isAuthenticated) {
        try {
          await startMeditation({
            duration: duration * 60,
            type: meditationType,
            intention: intention || 'Sacred meditation practice'
          });
          toast.success('🌟 Meditation session started');
        } catch (error) {
          console.error('Failed to start meditation:', error);
        }
      }
    } else {
      setIsPaused(!isPaused);
    }
  };

  const handleReset = () => {
    setIsActive(false);
    setIsPaused(false);
    setTimeLeft(duration * 60);
    setBreathCount(0);
    startTimeRef.current = null;
    stopAudio();
  };

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const progress = ((duration * 60 - timeLeft) / (duration * 60)) * 100;

  const getBreathPhase = () => {
    if (meditationType !== 'breath' || !isActive) return '';
    const phase = breathCount % 19;
    if (phase < 4) return 'Breathe In...';
    if (phase < 11) return 'Hold...';
    return 'Breathe Out...';
  };

  return (
    <div className="fr00t-meditation-timer">
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.8 }}
        style={{
          ...timerVars,
          background: `radial-gradient(circle at 20% 15%, ${selectedType.color}2b 0%, transparent 42%), linear-gradient(145deg, var(--timer-panel) 0%, var(--timer-panel-soft) 100%)`,
          borderRadius: '28px',
          padding: '2rem',
          border: `1px solid ${selectedType.color}66`,
          boxShadow: `0 20px 48px ${selectedType.color}1f, inset 0 1px 0 ${selectedType.color}3d`,
          backdropFilter: 'blur(20px)',
          fontFamily: "'Space Grotesk', 'Avenir Next', sans-serif"
        }}
      >
        {/* Meditation Type Selection */}
        {!isActive && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            style={{ marginBottom: '2rem' }}
          >
            <h3 style={{
              fontSize: '1.125rem',
              letterSpacing: '0.08em',
              textTransform: 'uppercase',
              fontWeight: '700',
              color: selectedType.color,
              marginBottom: '1rem',
              textAlign: 'center'
            }}>
              Choose Your Practice
            </h3>
            
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
              gap: '0.75rem',
              marginBottom: '1.5rem'
            }}>
              {meditationTypes.map(type => (
                <motion.button
                  key={type.id}
                  onClick={() => setMeditationType(type.id)}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  style={{
                    padding: '1rem 0.75rem',
                    background: meditationType === type.id
                      ? `linear-gradient(155deg, ${type.color}30 0%, ${type.color}14 100%)`
                      : 'linear-gradient(145deg, rgba(15, 23, 42, 0.75) 0%, rgba(15, 23, 42, 0.45) 100%)',
                    border: meditationType === type.id ? `1px solid ${type.color}` : '1px solid rgba(148, 163, 184, 0.28)',
                    borderRadius: meditationType === type.id ? '18px 10px 18px 10px' : '14px',
                    color: meditationType === type.id ? '#e2e8f0' : '#94a3b8',
                    fontSize: '0.875rem',
                    fontWeight: '600',
                    cursor: 'pointer',
                    textAlign: 'center',
                    boxShadow: meditationType === type.id ? `0 8px 22px ${type.color}2e` : 'none'
                  }}
                >
                  <div style={{ letterSpacing: '0.01em' }}>{type.name}</div>
                  <div style={{ fontSize: '0.75rem', marginTop: '4px', opacity: 0.8 }}>
                    {type.description}
                  </div>
                </motion.button>
              ))}
            </div>

            {/* Intention Input */}
            <div style={{ marginBottom: '1rem' }}>
              <label style={{
                display: 'block',
                color: selectedType.color,
                fontSize: '0.875rem',
                fontWeight: '600',
                marginBottom: '0.5rem'
              }}>
                Set Your Intention (Optional)
              </label>
              <input
                type="text"
                value={intention}
                onChange={(e) => setIntention(e.target.value)}
                placeholder="What brings you to meditation today?"
                style={{
                  width: '100%',
                  padding: '12px',
                  background: 'rgba(2, 8, 23, 0.65)',
                  border: `1px solid ${selectedType.color}5c`,
                  borderRadius: '14px',
                  color: '#f1f5f9',
                  fontSize: '0.875rem'
                }}
              />
            </div>

            {/* Duration Selection */}
            <div>
              <label style={{
                display: 'block',
                color: selectedType.color,
                fontSize: '0.875rem',
                fontWeight: '600',
                marginBottom: '0.5rem'
              }}>
                Duration: {duration} minutes
              </label>
              <input
                type="range"
                min="5"
                max="60"
                step="5"
                value={duration}
                onChange={(e) => {
                  const newDuration = parseInt(e.target.value);
                  setDuration(newDuration);
                  setTimeLeft(newDuration * 60);
                }}
                style={{
                  width: '100%',
                  height: '8px',
                  borderRadius: '4px',
                  outline: 'none',
                  background: `linear-gradient(to right, ${selectedType.color} 0%, ${selectedType.color} ${(duration - 5) / 55 * 100}%, rgba(100, 116, 139, 0.3) ${(duration - 5) / 55 * 100}%, rgba(100, 116, 139, 0.3) 100%)`
                }}
              />
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                fontSize: '0.75rem',
                color: 'var(--timer-text-muted)',
                marginTop: '0.5rem'
              }}>
                <span>5 min</span>
                <span>30 min</span>
                <span>60 min</span>
              </div>
            </div>
          </motion.div>
        )}

        {/* Timer Display */}
        <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
          <motion.div
            animate={{
              scale: isActive && !isPaused ? [1, 1.02, 1] : 1,
              boxShadow: isActive && !isPaused ?
                [`0 0 20px ${selectedType.color}40`, `0 0 40px ${selectedType.color}60`, `0 0 20px ${selectedType.color}40`] :
                `0 0 20px ${selectedType.color}40`
            }}
            transition={{ duration: 2, repeat: isActive && !isPaused ? Infinity : 0 }}
            style={{
              position: 'relative',
              width: 'min(230px, 70vw)',
              aspectRatio: '1 / 1',
              margin: '0 auto',
              borderRadius: '50%',
              background: `radial-gradient(circle at 35% 25%, ${selectedType.color}26 0%, rgba(2, 6, 23, 0.88) 65%)`,
              border: `3px solid ${selectedType.color}`,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              clipPath: 'polygon(10% 0%, 90% 0%, 100% 10%, 100% 90%, 90% 100%, 10% 100%, 0% 90%, 0% 10%)'
            }}
          >
            {/* Progress Ring */}
            <svg style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              transform: 'rotate(-90deg)'
            }}>
              <circle
                cx="100"
                cy="100"
                r="90"
                fill="none"
                stroke={`${selectedType.color}40`}
                strokeWidth="8"
              />
              <motion.circle
                cx="100"
                cy="100"
                r="90"
                fill="none"
                stroke={selectedType.color}
                strokeWidth="8"
                strokeDasharray={`${2 * Math.PI * 90}`}
                strokeDashoffset={`${2 * Math.PI * 90 * (1 - progress / 100)}`}
                strokeLinecap="round"
                animate={{
                  strokeDashoffset: 2 * Math.PI * 90 * (1 - progress / 100)
                }}
                transition={{ duration: 0.5 }}
              />
            </svg>

            <Clock size={24} color={selectedType.color} style={{ marginBottom: '0.5rem', zIndex: 1 }} />
            <div style={{
              fontSize: 'clamp(2rem, 6.5vw, 2.7rem)',
              fontWeight: '700',
              color: '#e2e8f0',
              fontFamily: "'JetBrains Mono', 'Fira Code', monospace",
              letterSpacing: '0.06em',
              zIndex: 1
            }}>
              {formatTime(timeLeft)}
            </div>
            
            {meditationType === 'breath' && isActive && !isPaused && (
              <motion.div
                animate={{ opacity: [0.6, 1, 0.6] }}
                transition={{ duration: 2, repeat: Infinity }}
                style={{
                  fontSize: '0.875rem',
                  color: selectedType.color,
                  marginTop: '0.5rem',
                  zIndex: 1
                }}
              >
                {getBreathPhase()}
              </motion.div>
            )}
          </motion.div>

          {isActive && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              style={{
                marginTop: '1rem',
                fontSize: '1rem',
                color: '#e2e8f0'
              }}
            >
              {isPaused ? 'Paused' : selectedType.name}
            </motion.div>
          )}
        </div>

        {/* Controls */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: isActive ? '1fr 1fr 1fr' : '1fr',
          gap: '1rem'
        }}>
          {isActive && (
            <motion.button
              onClick={handleReset}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '0.5rem',
                padding: '14px',
                background: 'rgba(239, 68, 68, 0.2)',
                border: '1px solid #ef4444',
                borderRadius: '999px',
                color: '#f87171',
                fontWeight: '600',
                cursor: 'pointer'
              }}
            >
              <RotateCcw size={20} />
              Reset
            </motion.button>
          )}

          <motion.button
            onClick={handleStart}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '0.5rem',
              padding: '14px',
              background: `linear-gradient(120deg, ${selectedType.color}2e 0%, ${selectedType.color}14 100%)`,
              border: `1px solid ${selectedType.color}`,
              borderRadius: '999px',
              color: '#e2e8f0',
              fontWeight: '600',
              cursor: 'pointer',
              gridColumn: isActive ? 'auto' : '1'
            }}
          >
            {isActive ? (isPaused ? <Play size={20} /> : <Pause size={20} />) : <Target size={20} />}
            {isActive ? (isPaused ? 'Resume' : 'Pause') : 'Begin Meditation'}
          </motion.button>

          {isActive && (
            <motion.button
              onClick={() => {
                const elapsedSeconds = startTimeRef.current ? startTimeRef.current - timeLeft : duration * 60 - timeLeft;
                handleComplete(elapsedSeconds);
              }}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '0.5rem',
                padding: '14px',
                background: 'rgba(16, 185, 129, 0.2)',
                border: '1px solid #10b981',
                borderRadius: '999px',
                color: '#10b981',
                fontWeight: '600',
                cursor: 'pointer'
              }}
            >
              <Heart size={20} />
              Complete
            </motion.button>
          )}
        </div>

        {/* Sacred Numerology */}
        <div style={{
          marginTop: '2rem',
          padding: '1rem',
          background: `linear-gradient(145deg, ${selectedType.color}14 0%, rgba(2, 6, 23, 0.45) 100%)`,
          borderRadius: '16px',
          border: `1px solid ${selectedType.color}52`,
          textAlign: 'center'
        }}>
          <div style={{
            fontSize: '0.75rem',
            color: selectedType.color,
            fontFamily: 'monospace',
            marginBottom: '0.5rem'
          }}>
            Sacred Frequency: {selectedType.frequency}Hz
          </div>
          <div style={{
            fontSize: '0.75rem',
            color: 'var(--timer-text-muted)'
          }}>
            Infinite Potential (00) • Meditation • Inner Peace
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default MeditationTimer;
